/**
 * URL search-param serialization for puzzle config.
 *
 * Used in two directions:
 *   1. Config screens hydrate their form state from useSearchParams() so a
 *      visitor landing on /puzzles/solve?type=win&jumps=4&noise=10 sees those
 *      knobs already selected.
 *   2. Submitting a config navigates to /puzzles/{mode}/play?<serialized>
 *      so the resulting puzzle is shareable as a single URL.
 *
 * Three modes share most of the schema. Solve and Watch are nearly identical
 * (Watch additionally carries the LLM provider + model id; the API key never
 * touches the URL and is read from sessionStorage at play time). Endless has
 * its own shape because the form holds per-type ranges plus a run-length cap.
 *
 * Conventions:
 *   - Short URL keys (`type` not `puzzle_type`) for human readability.
 *   - Number-valued knobs are stringified as base-10 integers.
 *   - Missing/blank params fall back to the screen's existing defaults.
 *   - parse() never throws on bad input; it returns nulls / defaults so a
 *     URL with one bad value still resolves the rest.
 */

const VALID_TYPES = new Set(['win', 'threat', 'passthrough', 'denial']);

function asInt(value, fallback = null) {
  if (value == null || value === '') return fallback;
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : fallback;
}

function asEnum(value, allowed, fallback) {
  return allowed.has(value) ? value : fallback;
}

// ── Solve / Watch (single-puzzle config) ────────────────────────────────────

/**
 * Parse a single-puzzle config (used by Solve and Watch). Returns an object
 * with every field present; missing keys become null so the consuming form
 * can apply its own defaults without ambiguity about "default" vs "absent".
 */
export function parseSinglePuzzleSearchParams(searchParams) {
  return {
    puzzle_type: asEnum(searchParams.get('type'), VALID_TYPES, null),
    num_jumps:   asInt(searchParams.get('jumps'), null),
    noise:       asInt(searchParams.get('noise'), null),
    max_jump_len: asInt(searchParams.get('maxlen'), null),
    player:      asInt(searchParams.get('player'), null),
    seed:        asInt(searchParams.get('seed'), null),
    // Watch-mode extras; null in Solve flows.
    llmProvider: searchParams.get('provider') || null,
    llmModel:    searchParams.get('model') || null,
  };
}

/**
 * Serialize the single-puzzle form values to a URLSearchParams. Skips
 * undefined / null / empty fields so the resulting URL only contains the
 * knobs the user actually set.
 */
export function serializeSinglePuzzleSearchParams(form) {
  const params = new URLSearchParams();
  if (form.puzzle_type) params.set('type', String(form.puzzle_type));
  if (form.num_jumps != null) params.set('jumps', String(form.num_jumps));
  if (form.noise != null)  params.set('noise', String(form.noise));
  if (form.max_jump_len != null) params.set('maxlen', String(form.max_jump_len));
  if (form.player != null) params.set('player', String(form.player));
  if (form.seed != null && form.seed !== '') params.set('seed', String(form.seed));
  if (form.llmProvider) params.set('provider', form.llmProvider);
  if (form.llmModel) params.set('model', form.llmModel);
  return params;
}

// ── Endless mode ────────────────────────────────────────────────────────────
//
// URL shape:
//   ?types=win,threat,passthrough,denial
//   &run=endless | twelve | twentyfour
//   &win_n=1-4   &win_noise=10  &win_maxlen=3
//   &threat_n=2-4
//   &passthrough_n=2-4
//   &denial_n=4-6
//
// We always serialise the per-type ranges (even for types the user disabled)
// so toggling a type back on later doesn't lose its previous range setting.

const VALID_RUN_LENGTHS = new Set(['endless', 'twelve', 'twentyfour']);

function parseRange(value, fallbackLo, fallbackHi) {
  if (!value) return { minN: fallbackLo, maxN: fallbackHi };
  const m = String(value).match(/^(\d+)-(\d+)$/);
  if (!m) return { minN: fallbackLo, maxN: fallbackHi };
  const lo = parseInt(m[1], 10);
  const hi = parseInt(m[2], 10);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
    return { minN: fallbackLo, maxN: fallbackHi };
  }
  return { minN: Math.min(lo, hi), maxN: Math.max(lo, hi) };
}

/**
 * Parse endless-mode params. Caller supplies per-type defaults (so this
 * helper doesn't need to embed knowledge of each type's UI range bounds).
 */
export function parseEndlessSearchParams(searchParams, defaults) {
  const typesRaw = searchParams.get('types');
  const enabledTypes = typesRaw
    ? typesRaw.split(',').map(s => s.trim()).filter(t => VALID_TYPES.has(t))
    : null;
  const runLengthId = asEnum(searchParams.get('run'), VALID_RUN_LENGTHS, null);

  const perType = {};
  for (const t of VALID_TYPES) {
    const def = defaults[t] || {};
    const range = parseRange(searchParams.get(`${t}_n`), def.minN, def.maxN);
    const entry = {
      enabled: enabledTypes ? enabledTypes.includes(t) : (def.enabled ?? true),
      minN: range.minN,
      maxN: range.maxN,
    };
    if (t === 'win') {
      entry.noise = asInt(searchParams.get('win_noise'), def.noise ?? 10);
      entry.maxJumpLen = asInt(searchParams.get('win_maxlen'), def.maxJumpLen ?? 3);
    }
    perType[t] = entry;
  }

  return {
    perType,
    runLengthId,
    enabledTypes,
  };
}

/**
 * Serialize endless-mode form values to URLSearchParams. Per-type ranges are
 * always emitted so flipping a type off and back on remembers its range.
 */
export function serializeEndlessSearchParams({ perType, runLengthId }) {
  const params = new URLSearchParams();
  const enabledList = [];
  for (const t of VALID_TYPES) {
    const cfg = perType?.[t];
    if (!cfg) continue;
    if (cfg.enabled) enabledList.push(t);
    if (cfg.minN != null && cfg.maxN != null) {
      params.set(`${t}_n`, `${cfg.minN}-${cfg.maxN}`);
    }
    if (t === 'win') {
      if (cfg.noise != null)      params.set('win_noise', String(cfg.noise));
      if (cfg.maxJumpLen != null) params.set('win_maxlen', String(cfg.maxJumpLen));
    }
  }
  if (enabledList.length > 0) params.set('types', enabledList.join(','));
  if (runLengthId) params.set('run', runLengthId);
  return params;
}

export const PUZZLE_PARAM_VALID_TYPES = VALID_TYPES;
export const PUZZLE_PARAM_VALID_RUN_LENGTHS = VALID_RUN_LENGTHS;
